import { useContext, useEffect, useState } from "react";
import { Box, IconButton, Stack, Typography, useMediaQuery } from "@mui/material";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";
import type { RootState } from "stores/application.store";
import { useGetUserDetailsQuery } from "features/api/userApi";
import ConfigurationsContext from "context/configurations/ConfigurationsContext";
import { useGetActiveMessagesQuery } from "./messagesApi";
import { MessagePreview } from "./MessagePreview";
import { pageForPath } from "./types";

export function MessageRegions({ outage = false }: { outage?: boolean }) {
	const { pathname } = useLocation();
	const { configurationsData } = useContext(ConfigurationsContext);
	const enabled = configurationsData?.featureFlags?.bannersAndAdsEnabled === true;
	const { data: user } = useGetUserDetailsQuery();
	const impersonation = useSelector((state: RootState) => state.impersonation.impersonationUser);
	const contextKey = JSON.stringify({
		user: user?.email,
		emulatedEmail: impersonation?.email,
		emulatedAccount: impersonation?.accountId
	});
	const { currentData: data, refetch } = useGetActiveMessagesQuery(
		{ page: pageForPath(pathname), contextKey },
		{
			skip: !enabled,
			pollingInterval: 60_000,
			refetchOnFocus: true,
			refetchOnMountOrArgChange: true
		}
	);
	const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
	const [index, setIndex] = useState(0);
	const [paused, setPaused] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [focused, setFocused] = useState(false);
	const interacting = hovered || focused;
	const [now, setNow] = useState(Date.now());
	const messages = enabled
		? (data?.messages ?? []).filter(
				(m) => (m.type === "Outage") === outage && Date.parse(m.startUtc) <= now && Date.parse(m.endUtc) > now
			)
		: [];
	const current = index % Math.max(messages.length, 1);

	useEffect(() => {
		if (enabled) void refetch();
	}, [enabled, pathname, refetch]);

	useEffect(() => {
		if (!enabled) return;
		const tick = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(tick);
	}, [enabled]);
	useEffect(() => {
		if (!enabled || !data?.nextChangeUtc) return;
		const delay = Math.min(2_147_483_647, Math.max(0, Date.parse(data.nextChangeUtc) - Date.now() + 100));
		const timer = window.setTimeout(() => {
			setNow(Date.now());
			void refetch();
		}, delay);
		return () => window.clearTimeout(timer);
	}, [data?.nextChangeUtc, enabled, refetch]);
	useEffect(() => {
		if (outage || paused || interacting || reducedMotion || messages.length < 2) return;
		const timer = window.setInterval(() => setIndex((value) => value + 1), 8000);
		return () => window.clearInterval(timer);
	}, [outage, paused, interacting, reducedMotion, messages.length]);

	const selected = messages[current];
	if (!selected) return null;
	if (outage)
		return (
			<Stack aria-label="Outage messages">
				{messages.map((message) => (
					<MessagePreview
						key={message.messageId}
						message={message}
					/>
				))}
			</Stack>
		);
	return (
		<Box
			aria-label="Banner messages"
			role="region"
			aria-roledescription="carousel"
			sx={{
				display: "flex",
				flexWrap: "wrap",
				alignItems: "center",
				backgroundColor: "#e2f7fc",
				color: "#006c90"
			}}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			onFocusCapture={() => setFocused(true)}
			onBlurCapture={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
			}}
		>
			<Box
				key={selected.messageId}
				sx={
					reducedMotion
						? { flex: "1 1 220px", minWidth: 0 }
						: {
								flex: "1 1 220px",
								minWidth: 0,
								animation: "message-fade 200ms ease-in",
								"@keyframes message-fade": { from: { opacity: 0 }, to: { opacity: 1 } }
							}
				}
			>
				<MessagePreview message={selected} />
			</Box>
			{messages.length > 1 && (
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="center"
					sx={{ marginLeft: "auto", flexShrink: 0 }}
				>
					<IconButton
						aria-label="Previous banner"
						onClick={() => setIndex((value) => (value + messages.length - 1) % messages.length)}
					>
						<NavigateBefore />
					</IconButton>
					<Typography variant="caption">
						{current + 1} / {messages.length}
					</Typography>
					<IconButton
						aria-label="Next banner"
						onClick={() => setIndex((value) => value + 1)}
					>
						<NavigateNext />
					</IconButton>
					{!reducedMotion && (
						<IconButton
							aria-label={paused ? "Play banners" : "Pause banners"}
							onClick={() => setPaused(!paused)}
						>
							{paused ? <PlayArrow /> : <Pause />}
						</IconButton>
					)}
				</Stack>
			)}
		</Box>
	);
}
